import React, { FC, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import { Dropdown, Input } from 'semantic-ui-react';
import MultipleMachineModelEditor from 'widgets/bussiness/multiple-selector/multiple-machine-model-editor';
import MultipleLocationEditor from 'widgets/bussiness/multiple-selector/multiple-location-editor';
import MultipleUsersEditor from 'widgets/bussiness/multiple-selector/multiple-user-editor';
import MultipleSelectionDropdown from 'widgets/editors/multiple-selection-dropdown';
import { connect } from 'redux-scaffolding-ts';
import { LocationDto } from 'stores/configuration/locations/locations-store';
import { UserDto } from 'stores/users/users-store';
import { MachineModelDto } from 'stores/configuration/machinery/machine-models-store';
import { CheckListDto, CheckListStore } from 'stores/configuration/events-n-requests/checklists-store';
import { EventTypeDto, EventTypesStore } from 'stores/configuration/events-workflow/event-types-store';

const INTERESTING_EVENT_TYPE_NAMES = ['Technical Support', 'Technical Training', 'Conversion', 'Relocation'];

const STATUS_OPTIONS = [
  { key: 'draft', text: 'Draft', value: 'Draft' },
  { key: 'planned', text: 'Planned', value: 'Planned' },
  { key: 'inprogress', text: 'In Progress', value: 'InProgress' },
  { key: 'completed', text: 'Completed', value: 'Completed' },
  { key: 'closed', text: 'Closed', value: 'Closed' }
];

export interface ChecklistFiltersValue {
  eventTypeIds?: string[];
  eventId?: string;
  location?: string | string[];
  instructor?: string | string[];
  machineModelIds?: string[];
  comment?: string;
  status?: string;
  checklistItemIds?: string[];
}

interface ChecklistFiltersProps extends WithTranslation {
  onFilterChange: (filters: ChecklistFiltersValue) => void;
  compact?: boolean;
  checklistStore?: CheckListStore;
  eventTypesStore?: EventTypesStore;
}

const ChecklistFilters: FC<ChecklistFiltersProps> = ({ t, onFilterChange, compact, checklistStore, eventTypesStore }) => {
  const [filters, setFilters] = useState<ChecklistFiltersValue>({});
  const [selectedMachineModels, setSelectedMachineModels] = useState<MachineModelDto[]>([]);
  const [selectedLocations, setSelectedLocations] = useState<LocationDto[]>([]);
  const [selectedInstructors, setSelectedInstructors] = useState<UserDto[]>([]);
  const [selectedEventTypeIds, setSelectedEventTypeIds] = useState<string[]>([]);
  const [selectedChecklistItems, setSelectedChecklistItems] = useState<string[]>([]);
  const [firstColumnIndexes, setFirstColumnIndexes] = useState<number[]>([]);
  const filterTimer = useRef<ReturnType<typeof setTimeout>>();
  const filtersRef = useRef<HTMLDivElement>(null);

  // The grid reflows with zoom/width, so the filters in the left column (next to the side menu) are detected at runtime.
  useLayoutEffect(() => {
    const container = filtersRef.current;
    if (!container) return;

    const detectFirstColumn = () => {
      const items = Array.from(container.children) as HTMLElement[];
      const minLeft = Math.min(...items.map(item => item.offsetLeft));
      const indexes = items.map((item, index) => (item.offsetLeft === minLeft ? index : -1)).filter(index => index >= 0);
      setFirstColumnIndexes(current => (current.join() === indexes.join() ? current : indexes));
    };

    detectFirstColumn();
    const observer = new ResizeObserver(detectFirstColumn);
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  const filterClassName = (index: number) =>
    firstColumnIndexes.includes(index) ? 'filter checklist-reports__filter--first-column' : 'filter';

  useEffect(() => {
    checklistStore?.getAllAsync({
      searchQuery: '',
      orderBy: [],
      skip: 0,
      take: 100000,
      filter: 'isCheckListNew eq true',
      parameters: { ShowDeleted: 'false' }
    });
  }, [checklistStore]);

  useEffect(() => {
    eventTypesStore?.getAllAsync({
      searchQuery: '',
      orderBy: [],
      skip: 0,
      take: 4,
      filter:
        "active eq true and (tolower(name) eq 'technical support' or tolower(name) eq 'technical training' or tolower(name) eq 'conversion' or tolower(name) eq 'relocation')"
    });
  }, [eventTypesStore]);

  const debouncedFilterKeys: Array<keyof ChecklistFiltersValue> = ['eventId', 'location', 'instructor', 'comment', 'checklistItemIds'];

  const update = (key: keyof ChecklistFiltersValue, value: string | string[] | undefined) => {
    const next = { ...filters, [key]: value || undefined };
    setFilters(next);

    if (debouncedFilterKeys.includes(key)) {
      if (filterTimer.current) clearTimeout(filterTimer.current);
      filterTimer.current = setTimeout(() => onFilterChange(next), 300);
      return;
    }

    onFilterChange(next);
  };

  const updateMachineModels = (value: MachineModelDto[]) => {
    setSelectedMachineModels(value || []);
    update(
      'machineModelIds',
      (value || []).map(machineModel => machineModel.id)
    );
  };

  const updateLocations = (value: LocationDto[]) => {
    setSelectedLocations(value || []);
    update(
      'location',
      (value || []).map(location => location.location)
    );
  };

  const updateInstructors = (value: UserDto[]) => {
    setSelectedInstructors(value || []);
    update(
      'instructor',
      (value || []).map(instructor => `${instructor.lastName}, ${instructor.firstName}`)
    );
  };

  const eventTypeItems = (eventTypesStore?.state.items || []).map(item => item.item as EventTypeDto);
  const eventTypeOptions = eventTypeItems.map(item => ({
    id: item.id,
    text: item.name,
    value: item.id
  }));
  const checklistItems = (checklistStore?.state.items || []).map(item => item.item as CheckListDto);
  const availableChecklistItems = checklistItems.filter(item => {
    const isNewChecklist = item.isCheckListNew === true;
    const normalizedEventType = (item.eventTypeName || '').trim().toLowerCase();
    const isAllowedEventType = INTERESTING_EVENT_TYPE_NAMES.some(name => name.toLowerCase() === normalizedEventType);
    const matchesSelectedEventType = selectedEventTypeIds.length === 0 || selectedEventTypeIds.includes(item.eventTypeId);
    return isNewChecklist && isAllowedEventType && matchesSelectedEventType;
  });
  const checklistOptions = availableChecklistItems.map(item => ({
    id: item.id,
    key: item.id,
    text: `${item.eventTypeName} - ${item.description}`,
    value: item.id
  }));

  return (
    <div ref={filtersRef} className="checklist-reports__filters" style={{ width: '100%', marginBottom: compact ? 0 : 12 }}>
      <MultipleSelectionDropdown
        searchable
        clearable
        options={eventTypeOptions}
        value={selectedEventTypeIds}
        placeholder={t('Event Type')}
        onChange={value => {
          const eventTypeIds = value as string[];
          setSelectedEventTypeIds(eventTypeIds);
          setSelectedChecklistItems([]);
          const next = { ...filters, eventTypeIds: eventTypeIds.length > 0 ? eventTypeIds : undefined, checklistItemIds: undefined };
          setFilters(next);
          onFilterChange(next);
        }}
        className={filterClassName(0)}
      />
      <Input
        icon="search"
        iconPosition="left"
        placeholder={t('Event ID')}
        className="filter"
        onChange={(_, { value }) => update('eventId', value)}
      />
      <MultipleMachineModelEditor
        clearable
        placeholder={t('Machine Model')}
        className={filterClassName(2)}
        value={selectedMachineModels}
        onChange={updateMachineModels}
      />
      <MultipleLocationEditor
        clearable
        showCountry={false}
        placeholder={t('Location')}
        className={filterClassName(3)}
        value={selectedLocations}
        onChange={updateLocations}
      />
      <MultipleUsersEditor
        clearable
        placeholder={t('Instructor')}
        role="Instructor"
        className={filterClassName(4)}
        value={selectedInstructors}
        onChange={updateInstructors}
      />
      <Dropdown
        search
        selection
        clearable
        closeOnChange
        options={STATUS_OPTIONS}
        placeholder={t('Status')}
        onChange={(_, { value }) => update('status', value as string)}
        selectOnBlur={false}
        className="filter"
      />
      <MultipleSelectionDropdown
        searchable
        clearable
        options={checklistOptions}
        value={selectedChecklistItems}
        placeholder={t('CheckList Item')}
        className={filterClassName(6)}
        onChange={value => {
          const checklistItems = value as string[];
          setSelectedChecklistItems(checklistItems);
          update('checklistItemIds', checklistItems);
        }}
      />
      <Input
        icon="search"
        iconPosition="left"
        placeholder={t('Comment')}
        className="filter"
        onChange={(_, { value }) => update('comment', value)}
      />
    </div>
  );
};

export default connect(['checklistStore', CheckListStore], ['eventTypesStore', EventTypesStore])(withTranslation()(ChecklistFilters));
